// src/pages/Borrowers.jsx
// SEO-optimised borrowers page for Dynesourcer
// Target keywords: buy-to-let mortgage, BTL mortgage comparison, landlord mortgage UK,
// HMO mortgage, portfolio landlord mortgage, limited company buy-to-let, BTL calculator,
// buy-to-let mortgage rates UK, specialist BTL broker

import React from "react";
import TopNav from "../../components/TopNav.jsx";
import SiteFooter from "../../components/SiteFooter.jsx";
import {
  Check,
  Search,
  ArrowRight,
  Home as HomeIcon,
  FileText,
  Zap,
  TrendingUp,
  Lock,
  Calculator,
  BadgeCheck,
  Building2,
  Shield,
} from "lucide-react";
import { Link } from "react-router-dom";

const BORROWERS_HERO_SRC = "/Borrowers.webp";

const borrowerProducts = [
  { key: "btl", title: "Buy-to-Let Mortgages", active: true, href: "/buy-to-let-mortgage-calculator", icon: "home" },
  { key: "invoice", title: "Invoice Finance", active: true, href: "/invoice-finance/invoice-finance-sourcing", icon: "file" },
  { key: "complex_btl", title: "Complex BTL", active: true, href: "/mortgages/complex-buy-to-let-mortgage-calculator", icon: "home" },
  { key: "semi_commercial", title: "Semi-commercial", active: false, href: "/semi-commercial-calculator-uk", icon: "building" },
  { key: "commercial", title: "Commercial", active: false, href: "/commercial-calculator-uk", icon: "building" },
  { key: "bridging", title: "Bridging Finance", active: false, href: "/sourcing/bridging", icon: "zap" },
  { key: "development", title: "Development Finance", active: false, href: "/sourcing/development", icon: "trend" },
];

function ProductCard({ icon, title, desc, bullets, href = "#", active = false, ctaLabel = "Source Now" }) {
  return (
    <article
      className={
        "group rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:shadow-lg hover:-translate-y-0.5 hover:ring-1 hover:ring-sky-200" +
        (active ? "" : " opacity-60")
      }
    >
      <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-sky-50 text-sky-600">
        {icon}
      </div>
      <h3 className="mt-4 text-xl font-black text-slate-900">{title}</h3>
      <p className="mt-2 text-slate-600">{desc}</p>
      <ul className="mt-4 space-y-2 text-sm text-slate-700">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2">
            <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-sky-500" /> {b}
          </li>
        ))}
      </ul>
      <div className="mt-6 flex items-center gap-3">
        {active ? (
          <Link
            to={href}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-sky-500 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-600 transition-colors"
          >
            <Search className="h-4 w-4" /> {ctaLabel}
          </Link>
        ) : (
          <span
            className="inline-flex items-center justify-center gap-2 rounded-md bg-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 cursor-not-allowed"
            title="Coming soon"
            aria-disabled="true"
          >
            <Lock className="h-4 w-4" /> Coming soon
          </span>
        )}
      </div>
    </article>
  );
}

export default function Borrowers() {
  React.useEffect(() => {
    document.title = "Buy-to-Let Mortgage Sourcing for Landlords & Property Investors | Dynesourcer";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Compare buy-to-let mortgage rates matched to your eligibility. Source HMO, portfolio landlord and limited company BTL mortgages online. Free to enquire — expert broker reviews every application before submission."
      );
    }
  }, []);

  React.useEffect(() => {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = BORROWERS_HERO_SRC;
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  return (
    <main className="min-h-screen bg-white text-slate-800">
      <TopNav />

      {/* ── Hero ── */}
      <section
        className="relative h-[520px] w-full overflow-hidden"
        aria-label="Buy-to-let mortgage sourcing for landlords and property investors"
      >
        <h1 className="sr-only">
          Buy-to-Let Mortgage Sourcing for Landlords &amp; Property Investors | Dynesourcer
        </h1>
        <img
          src={BORROWERS_HERO_SRC}
          alt="Landlord sourcing buy-to-let mortgage products online with Dynesourcer"
          className="absolute inset-0 h-full w-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-sky-900/55" />
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-6">
          <div className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-bold uppercase tracking-widest mb-4">
            Free to enquire online
          </div>
          <h2 className="text-4xl md:text-6xl font-black tracking-tight leading-tight max-w-4xl">
            Source buy-to-let mortgages<br className="hidden md:block" /> matched to your eligibility
          </h2>
          <p className="mt-5 max-w-2xl text-white/90 text-lg leading-relaxed">
            Compare BTL mortgage rates by LTV, ICR, property type and borrower structure.
            Source HMO, portfolio and limited company products — then apply online.
            Our broker team review every case before it reaches the lender.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 items-center justify-center">
            <Link
              to="/sourcing/buy-to-let-mortgage-sourcing"
              className="inline-flex items-center gap-2 rounded-lg bg-sky-500 px-7 py-3.5 text-base font-bold text-white hover:bg-sky-600 transition-colors shadow-lg"
            >
              <Search className="h-5 w-5" /> Compare BTL mortgage rates
            </Link>
            <Link
              to="/buy-to-let-mortgage-calculator"
              className="inline-flex items-center gap-2 rounded-lg bg-white/15 backdrop-blur-sm border border-white/30 px-7 py-3.5 text-base font-semibold text-white hover:bg-white/25 transition-colors"
            >
              <Calculator className="h-5 w-5" /> BTL calculator
            </Link>
          </div>
          <p className="mt-4 text-xs text-white/70">
            Free to source &amp; enquire &nbsp;•&nbsp; Broker fee applies on full application &nbsp;•&nbsp; No obligation
          </p>
        </div>
      </section>

      {/* ── Trust signals ── */}
      <div className="bg-sky-600 text-white py-3">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-nowrap justify-center gap-x-5 text-xs md:text-sm font-semibold">
            {[
              "Whole-of-market BTL sourcing",
              "HMO & portfolio landlord criteria",
              "Limited company (SPV) mortgages",
              "Free to enquire online",
              "Expert broker triage on every case",
            ].map((item) => (
              <span key={item} className="flex items-center gap-2 whitespace-nowrap">
                <Check className="h-4 w-4 flex-shrink-0" /> {item}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Why Dynesourcer ── */}
      <section
        className="py-16 md:py-24 bg-white"
        aria-label="Why landlords use Dynesourcer for BTL mortgage sourcing"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900">
              Why landlords choose Dynesourcer
            </h2>
            <p className="mt-3 text-lg text-slate-600">
              We combine intelligent buy-to-let mortgage sourcing with real broker expertise —
              so you don't just find a rate, you get the deal done.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: <Search className="h-6 w-6" />,
                title: "Eligibility-first matching",
                desc: "Only see BTL mortgage products you actually qualify for — filtered by LTV, ICR, property type and borrower profile.",
              },
              {
                icon: <Calculator className="h-6 w-6" />,
                title: "Free BTL calculator",
                desc: "Calculate monthly repayments, interest cover ratio, arrangement fee impact and true cost before you apply.",
              },
              {
                icon: <Building2 className="h-6 w-6" />,
                title: "Specialist criteria covered",
                desc: "HMO mortgages, MUFBs, portfolio landlords, limited company SPVs and first-time landlords all supported.",
              },
              {
                icon: <BadgeCheck className="h-6 w-6" />,
                title: "Broker reviews every case",
                desc: "A qualified mortgage broker validates your application and confirms product fit before submission — reducing declines.",
              },
            ].map((it) => (
              <div
                key={it.title}
                className="rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all"
              >
                <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-xl bg-sky-50 text-sky-600 mb-4">
                  {it.icon}
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{it.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{it.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section
        className="py-16 md:py-20 bg-sky-50"
        aria-label="How buy-to-let mortgage sourcing works on Dynesourcer"
      >
        <div className="mx-auto max-w-6xl px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-slate-900">
            How to source a buy-to-let mortgage on Dynesourcer
          </h2>
          <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
            From comparison to application in four straightforward steps — built for landlords
            who want transparency, not back-and-forth phone calls.
          </p>

          <div className="mt-10 grid gap-6 md:grid-cols-4">
            {[
              {
                num: "1",
                title: "Enter your BTL requirements",
                desc: "Property value, deposit, loan amount, rental income, property type and borrower structure (individual, portfolio or limited company).",
              },
              {
                num: "2",
                title: "Compare matched BTL products",
                desc: "See live buy-to-let mortgage rates you're eligible for. Use our free BTL calculator to compare true cost, ICR and arrangement fees side by side.",
              },
              {
                num: "3",
                title: "Submit a free online enquiry",
                desc: "Shortlist your preferred products and submit an online enquiry — free with no obligation. Our broker team will review your case.",
              },
              {
                num: "4",
                title: "Broker validates & submits",
                desc: "Once you proceed, your broker prepares and submits the full application to the lender. A broker fee applies at this stage — you'll be informed upfront.",
              },
            ].map((step, idx) => (
              <div
                key={idx}
                className="rounded-xl bg-white p-6 shadow-sm border border-sky-100 hover:shadow-lg hover:-translate-y-0.5 transition-all text-left"
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-sky-500 text-white font-black text-lg mb-4">
                  {step.num}
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-10">
            <Link
              to="/sourcing/buy-to-let-mortgage-sourcing"
              className="inline-flex items-center gap-2 rounded-full bg-sky-500 px-8 py-4 text-base font-bold text-white hover:bg-sky-600 transition-colors shadow-lg"
            >
              Start sourcing BTL mortgages <ArrowRight className="h-5 w-5" />
            </Link>
            <p className="mt-3 text-sm text-slate-500">Free to source &amp; enquire — broker fee applies on full application</p>
          </div>
        </div>
      </section>

      {/* ── Products ── */}
      <section
        id="products"
        className="bg-sky-50 py-16 md:py-20"
        aria-label="Buy-to-let and specialist mortgage products on Dynesourcer"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900">
              Specialist mortgage products for property investors
            </h2>
            <p className="mt-2 text-slate-600">
              Buy-to-let, Invoice Finance and Complex BTL sourcing are live now. Bridging and development
              finance are coming soon.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {borrowerProducts.map((p) => (
              <ProductCard
                key={p.key}
                icon={
                  p.icon === "home" ? <HomeIcon className="h-5 w-5" /> :
                  p.icon === "file" ? <FileText className="h-5 w-5" /> :
                  p.icon === "zap"  ? <Zap className="h-5 w-5" /> :
                  p.icon === "building" ? <Building2 className="h-5 w-5" /> :
                                      <TrendingUp className="h-5 w-5" />
                }
                title={p.title}
                desc={
                  p.key === "btl"
                    ? "Compare buy-to-let mortgage rates from across the UK market. Source purchase, remortgage and product transfer deals for individual landlords, portfolio investors and limited companies."
                    : p.key === "invoice"
                      ? "Release working capital tied up in unpaid invoices. Flexible selective and whole-ledger facilities for property and construction businesses."
                      : p.key === "complex_btl"
                        ? "Specialist BTL mortgages for HMOs, MUFBs and complex borrower structures including portfolio landlords and SPVs."
                        : p.key === "semi_commercial"
                          ? "Finance for mixed-use properties with residential and commercial elements — shops with flats above, and similar semi-commercial assets."
                          : p.key === "commercial"
                            ? "Commercial mortgage sourcing for offices, retail, industrial and investment properties with tailored lender criteria."
                            : p.key === "bridging"
                              ? "Fast short-term bridging loans for auction purchases, refurbishment and chain-break scenarios — regulated and unregulated."
                              : "Ground-up development and heavy refurbishment finance with staged drawdowns and flexible exit strategies."
                }
                bullets={
                  p.key === "btl"
                    ? ["Up to 75% LTV available", "Portfolio & limited company landlords", "Free BTL calculator included"]
                    : p.key === "invoice"
                      ? ["Advance up to 90% of invoice value", "Selective or full ledger", "Fast 24–48 hr onboarding"]
                      : p.key === "complex_btl"
                        ? ["HMO & MUFB specialist criteria", "SPV / Ltd company structures", "Flexible ICR & DSCR calculations"]
                        : p.key === "semi_commercial"
                          ? ["Mixed-use & shop-with-flat", "Flexible income assessment", "Specialist lender panel"]
                          : p.key === "commercial"
                            ? ["Office, retail & industrial", "Investment & owner-occupy", "Tailored underwriting"]
                            : p.key === "bridging"
                              ? ["Terms from 1–24 months", "Quick credit decisions", "Regulated & unregulated"]
                              : ["Staged drawdown funding", "Independent monitoring surveyor", "Flexible exit planning"]
                }
                href={p.href}
                active={p.active}
                ctaLabel={
                  p.key === "btl"
                    ? "Use BTL calculator"
                    : p.key === "invoice"
                      ? "Source invoice finance"
                      : "Source Now"
                }
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── Who we help ── */}
      <section
        className="py-16 md:py-20 bg-white"
        aria-label="Types of buy-to-let borrowers Dynesourcer supports"
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900">
              BTL mortgages for every type of landlord
            </h2>
            <p className="mt-3 text-lg text-slate-600">
              Whether you're buying your first rental property or refinancing a large portfolio,
              our sourcing platform covers the full spectrum of buy-to-let borrower profiles.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                title: "First-time landlords",
                desc: "New to buy-to-let? We match you to lenders who accept first-time landlords, with clear eligibility criteria and guidance throughout.",
                bullets: ["No BTL experience required with some lenders", "25% deposit typically needed", "Residential ownership not always required"],
              },
              {
                title: "Portfolio landlords",
                desc: "Own four or more mortgaged properties? We source lenders with specialist portfolio underwriting who assess your full portfolio position.",
                bullets: ["Whole portfolio ICR assessment", "Background portfolio accepted", "Up to 75% LTV on individual properties"],
              },
              {
                title: "HMO & MUFB investors",
                desc: "Houses in Multiple Occupation and multi-unit freehold blocks require specialist criteria — our sourcing tool filters these automatically.",
                bullets: ["Licensed HMO mortgages", "Multi-unit block (MUFB) criteria", "Higher rental yield calculations accepted"],
              },
              {
                title: "Limited company (SPV) landlords",
                desc: "Source buy-to-let mortgages for Special Purpose Vehicles and trading limited companies — increasingly popular for tax efficiency.",
                bullets: ["SPV & trading Ltd company accepted", "Director personal guarantees standard", "Corporation tax-efficient structure"],
              },
              {
                title: "Remortgage & product transfer",
                desc: "Coming off a fixed rate? Compare the whole market for your remortgage rather than defaulting to your lender's standard variable rate.",
                bullets: ["Beat your lender's revert rate", "Capital raising on equity", "No early repayment charge check included"],
              },
              {
                title: "Complex income landlords",
                desc: "Self-employed, contractor, foreign national or with adverse credit history? We source specialist lenders who consider complex cases.",
                bullets: ["Self-employed with 1 year accounts", "Foreign national BTL mortgages", "Adverse credit assessed case-by-case"],
              },
            ].map((card) => (
              <article
                key={card.title}
                className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all"
              >
                <h3 className="text-lg font-bold text-slate-900 mb-2">{card.title}</h3>
                <p className="text-sm text-slate-600 mb-4 leading-relaxed">{card.desc}</p>
                <ul className="space-y-2">
                  {card.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-sky-500" /> {b}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Fee transparency band ── */}
      <section className="bg-slate-50 border-y border-slate-200 py-10">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-sky-100 mb-4">
            <Shield className="h-6 w-6 text-sky-600" />
          </div>
          <h2 className="text-xl font-black text-slate-900 mb-2">Transparent about fees</h2>
          <p className="text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Sourcing buy-to-let mortgage products and submitting an online enquiry through
            Dynesourcer is <strong>completely free</strong> with no obligation. A broker fee
            applies when you choose to proceed to a full mortgage application — covering expert
            case preparation, lender submission and end-to-end support. You'll always be informed
            of the fee before you commit.
          </p>
        </div>
      </section>

      {/* ── CTA band ── */}
      <section
        className="bg-sky-400 py-16 text-center text-white"
        aria-label="Start sourcing buy-to-let mortgages"
      >
        <h2 className="text-3xl md:text-4xl font-black">
          Ready to find your next buy-to-let mortgage?
        </h2>
        <p className="mt-3 text-white/90 max-w-xl mx-auto">
          Compare BTL mortgage rates matched to your eligibility — purchase, remortgage, HMO,
          portfolio or limited company. Free to source and enquire.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/sourcing/buy-to-let-mortgage-sourcing"
            className="inline-flex items-center gap-2 bg-white text-sky-700 font-bold px-7 py-3.5 rounded-lg hover:bg-slate-100 transition-colors shadow-lg"
          >
            <Search className="h-5 w-5" /> Compare BTL mortgage rates
          </Link>
          <Link
            to="/buy-to-let-mortgage-calculator"
            className="inline-flex items-center gap-2 bg-white/15 border border-white/30 text-white font-semibold px-7 py-3.5 rounded-lg hover:bg-white/25 transition-colors"
          >
            <Calculator className="h-5 w-5" /> BTL mortgage calculator
          </Link>
        </div>
        <p className="mt-4 text-xs text-white/70">
          Free to source &amp; enquire &nbsp;•&nbsp; Broker fee applies on full application &nbsp;•&nbsp; No obligation
        </p>
      </section>

      <SiteFooter />
    </main>
  );
}